import { useSelector } from 'react-redux';

import { filter, get, map } from 'lodash';

import { useCurrentUser } from '@lib/hooks';
import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';
import { selectCurrentMasterSettings } from '@lib/selectors';

import usePermissions from 'hooks/usePermissions';

import { SETTINGS_ITEMS, Tab } from 'screens/MissionControl/constants/settingsItems';
import useControlUserAccess from 'screens/MissionControl/hooks/useControlUserAccess';
import { isUsingOutreachExperience } from 'utils/outreachExperience';

import useIntegrationHealthMenu from './useIntegrationHealthMenu';

export default function useAccessControlItems() {
  const masterSettings = useSelector(selectCurrentMasterSettings);
  const isToUseOutreachExperience = isUsingOutreachExperience(masterSettings);
  const { permissions, hasAccessToProduct } = usePermissions();
  const currentUser = useCurrentUser();
  const { filterIntegrationHealthMenu, firstEnabledIntegrator } = useIntegrationHealthMenu();

  const { securityCenter, practiceResources, featureSettings, utilities, ehrIntegration } = useControlUserAccess();

  // insurance-verification provisioning allow access to luma pay stuff
  const hasAccessToLumaPay = utilities.patientBilling.canView && hasAccessToProduct([ProvisioningEnum.LumaPay]);
  const hasAccessToPayoutsAndLumaPaySettings = utilities.lumaPay.canView;

  const hasAccessToDesignatedNumbers = utilities.designatedNumbers.canView;
  const hasAccessToChecklistBuilder = utilities.checklistBuilder.canView;
  const hasAccessToFaxTransformAutomations = utilities.faxTransformAutomations.canView;
  const hasAccessToAssistants = utilities.assistants.canView;

  const hasAccessToMultipleAccountManagement = securityCenter.multipleAccountManagement.canView;
  const hasAccessToFollowupWorkflows = utilities.followupWorkflows.canView;
  const hasAccessToMessagePartials = utilities.messagePartials.canView;

  const hasAccessToSpecialty =
    hasAccessToProduct(RolloutEnum.SpecialtyReferrals) && get(currentUser, 'settings.referral.useSpecialty.enabled');
  const hasAccessToBroadcastTemplates =
    hasAccessToProduct(['broadcast-template-builder' as RolloutEnum]) &&
    (permissions?.isAdmin || permissions?.isManager);

  return SETTINGS_ITEMS.filter((item) => {
    if (
      (item.key === 'security-center' && !securityCenter.canView) ||
      (item.key === 'practice-resources' && !practiceResources.canView) ||
      (item.key === 'features' && !featureSettings.canView) ||
      (item.key === 'utilities' && !utilities.canView) ||
      (item.key === 'ehr-integration' && !ehrIntegration.canView) ||
      // Luma Admin will have access o ehr-integration instead of integration-health
      (item.key === 'integration-health' && ehrIntegration.canView)
    ) {
      return false;
    }

    return true;
  }).map((item) => {
    if (item.key === 'utilities' || item.key === 'security-center') {
      const utilitiesSubMenuFilter = (mainSubMenu: typeof item): any =>
        mainSubMenu.subMenu && mainSubMenu.subMenu.length > 0
          ? {
              ...mainSubMenu,
              ...(mainSubMenu.subMenu && {
                subMenu: filter(
                  map(mainSubMenu.subMenu, (subSubMenu) => utilitiesSubMenuFilter(subSubMenu)),
                  (subMenu) => {
                    if (subMenu.key === 'branding') {
                      utilitiesSubMenuFilter(subMenu);
                    }

                    switch (subMenu.key) {
                      case 'outreach':
                        return isToUseOutreachExperience;
                      case 'patient-billing':
                      case 'billing-items':
                      case 'billing-vendor':
                        return hasAccessToLumaPay;
                      case 'payouts':
                      case 'lumapay-settings':
                        return hasAccessToPayoutsAndLumaPaySettings;
                      case 'outbound-numbers':
                        return hasAccessToDesignatedNumbers;
                      case 'checklist-builder':
                        return hasAccessToChecklistBuilder;
                      case 'message-template-partials':
                        return hasAccessToMessagePartials;
                      case 'followup-workflows':
                        if (hasAccessToProduct(RolloutEnum.NextyFollowupWorkflows)) {
                          return false;
                        }
                        return hasAccessToFollowupWorkflows;
                      case 'estimates':
                        return false; // The estimates feature is still being build
                      case 'multiple-account-management':
                        return hasAccessToMultipleAccountManagement;
                      case 'tokens':
                        return securityCenter.tokens.canView;
                      case 'cobranding':
                        return utilities.branding.cobranding.canView;
                      case 'eligibility-setup':
                        return utilities.eligibilitySetup.canView;
                      case 'broadcast-templates':
                        return hasAccessToBroadcastTemplates;

                      default:
                        return (
                          (!subMenu.provisioning?.length || hasAccessToProduct(subMenu.provisioning)) &&
                          (!subMenu.provisioningExcluded?.length || !hasAccessToProduct(subMenu.provisioningExcluded))
                        );
                    }
                  },
                ),
              }),
            }
          : mainSubMenu;

      return utilitiesSubMenuFilter(item);
    }

    if (item.key === 'features' || item.key === 'security-center') {
      return {
        ...item,
        subMenu: filter(item.subMenu, (subMenu) =>
          subMenu.provisioning.length !== 0 ? hasAccessToProduct(subMenu.provisioning) : true,
        ).map((subMenu) =>
          isToUseOutreachExperience && subMenu.label === 'Inbound Referral'
            ? { ...subMenu, label: 'Outreach' }
            : subMenu,
        ),
      };
    }

    if (item.key === 'spark-management') {
      return {
        ...item,
        subMenu: filter(item.subMenu, (subMenu) => {
          if (subMenu.key === 'fax-transform-automations') {
            return hasAccessToFaxTransformAutomations;
          }

          if (subMenu.key === 'assistants') {
            return hasAccessToAssistants;
          }
        }),
      };
    }

    if (item.key === 'practice-resources') {
      return {
        ...item,
        subMenu: filter(item.subMenu, (subMenu) => (subMenu.key === 'specialties' ? hasAccessToSpecialty : true)),
      };
    }

    if (item.key === 'integration-health') {
      return {
        ...item,
        subMenu: filter(item.subMenu, (subMenu) => filterIntegrationHealthMenu(subMenu.key)),
      };
    }

    if (item.key === 'ehr-integration') {
      return {
        ...item,
        subMenu: map(
          filter(item.subMenu, (subMenu) => filterIntegrationHealthMenu(subMenu.key)),
          (subMenu: Tab) =>
            subMenu.key === 'overview' && firstEnabledIntegrator
              ? { ...subMenu, path: `/v2/settings/ehr-integration/overview/${firstEnabledIntegrator._id}` }
              : subMenu,
        ),
      };
    }

    return item;
  });
}
